import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { customerService, type CaptchaChallenge } from '@/services/customer.service';

/**
 * Server-side math captcha. Fetches a challenge, exposes its token to the parent,
 * and collects the user's answer. The answer is validated server-side on submit.
 */
export function MathCaptcha({
  answer,
  onAnswerChange,
  onTokenChange,
  disabled,
  fetchChallenge,
  queryKey = 'captcha',
}: {
  answer: string;
  onAnswerChange: (v: string) => void;
  onTokenChange: (token: string) => void;
  disabled?: boolean;
  /**
   * D-099 — where to get the challenge. Defaults to the tenant-scoped customer
   * endpoint, so every existing caller is unchanged; the login form passes the
   * anonymous one, which is the only challenge an unauthenticated page can get.
   */
  fetchChallenge?: () => Promise<CaptchaChallenge>;
  /** Distinct cache key per source, so the two challenges never share a cache entry. */
  queryKey?: string;
}) {
  const { data, isFetching, refetch } = useQuery<CaptchaChallenge>({
    queryKey: [queryKey],
    queryFn: async () => {
      const c = await (fetchChallenge ? fetchChallenge() : customerService.captcha());
      onTokenChange(c.token);
      return c;
    },
    staleTime: 0,
    gcTime: 0,
  });

  return (
    <FormField label="Security check" htmlFor="captcha-answer" required>
      <div className="flex items-center gap-2">
        <span className="inline-flex h-10 min-w-[90px] items-center justify-center rounded-lg border border-border bg-muted/40 px-3 font-mono text-sm font-semibold text-foreground">
          {isFetching ? <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" /> : (data?.question ?? '…')}
        </span>
        <Input
          id="captcha-answer"
          value={answer}
          onChange={(e) => onAnswerChange(e.target.value)}
          placeholder="Answer"
          disabled={disabled}
          className="max-w-[120px]"
          autoComplete="off"
          inputMode="numeric"
        />
        <button
          type="button"
          onClick={() => {
            onAnswerChange('');
            void refetch();
          }}
          disabled={disabled}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="New captcha"
          title="New captcha"
        >
          <FontAwesomeIcon icon={faRotate} className="h-3.5 w-3.5" />
        </button>
      </div>
    </FormField>
  );
}
